import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  Briefcase, 
  Share2, 
  TrendingUp, 
  Plus,
  ArrowRight,
  Calendar,
  Building2
} from 'lucide-react';
import { formatDate, getStatusColor, capitalizeFirst } from '../lib/utils';
import DashboardLayout from '../components/DashboardLayout';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await dashboardAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.debug('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    {
      title: 'Resumes',
      value: stats?.resume_count || 0,
      icon: <FileText className="w-5 h-5" />,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      link: '/resumes',
    },
    {
      title: 'Applications',
      value: stats?.total_applications || 0,
      icon: <Briefcase className="w-5 h-5" />,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      link: '/jobs',
    },
    {
      title: 'Interviews',
      value: stats?.status_counts?.interview || 0,
      icon: <Calendar className="w-5 h-5" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      link: '/jobs',
    },
    {
      title: 'Offers',
      value: stats?.status_counts?.offer || 0,
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      link: '/jobs',
    },
  ];

  const quickActions = [
    {
      title: 'Create Resume',
      description: 'Build a new AI-powered resume',
      icon: <FileText className="w-6 h-6" />,
      link: '/resumes/new',
      color: 'bg-indigo-600 hover:bg-indigo-700',
    },
    {
      title: 'Track Application',
      description: 'Add a new job application',
      icon: <Briefcase className="w-6 h-6" />,
      link: '/jobs/new',
      color: 'bg-emerald-600 hover:bg-emerald-700',
    },
    {
      title: 'Repurpose Content',
      description: 'Create social media posts',
      icon: <Share2 className="w-6 h-6" />,
      link: '/repurpose',
      color: 'bg-purple-600 hover:bg-purple-700',
    },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600 mt-1">
              Here's what's happening with your career journey
            </p>
          </div>
          <Link to="/resumes/new">
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-resume-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Resume
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Link to={stat.link} key={index}>
              <Card className="card-hover cursor-pointer" data-testid={`stat-card-${stat.title.toLowerCase()}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">{stat.title}</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.bgColor} ${stat.color} flex items-center justify-center`}>
                      {stat.icon}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {quickActions.map((action, index) => (
                  <Link to={action.link} key={index}>
                    <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer" data-testid={`quick-action-${index}`}>
                      <div className={`w-12 h-12 rounded-xl ${action.color} text-white flex items-center justify-center`}>
                        {action.icon}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{action.title}</p>
                        <p className="text-sm text-slate-600">{action.description}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </Link>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Recent Applications */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Recent Applications
                </CardTitle>
                <Link to="/jobs">
                  <Button variant="ghost" size="sm" data-testid="view-all-jobs-btn">
                    View All
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="animate-pulse flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                        <div className="w-12 h-12 bg-slate-200 rounded-xl"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                          <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : stats?.recent_jobs?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_jobs.map((job) => (
                      <div
                        key={job.id}
                        className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                        data-testid={`recent-job-${job.id}`}
                      >
                        <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{job.position}</p>
                          <p className="text-sm text-slate-600 truncate">{job.company}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(job.status)}`}>
                            {capitalizeFirst(job.status)}
                          </span>
                          <span className="text-xs text-slate-500">
                            {formatDate(job.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Briefcase className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-600 mb-4">No applications yet</p>
                    <Link to="/jobs/new">
                      <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-first-job-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Your First Application
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Application Status Overview */}
        {stats?.total_applications > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Application Status Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(stats.status_counts).map(([status, count]) => (
                  <div
                    key={status}
                    className={`p-4 rounded-xl border ${getStatusColor(status)}`}
                    data-testid={`status-${status}`}
                  >
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm font-medium">{capitalizeFirst(status)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
